import React, { useCallback, useEffect, useState } from 'react';
import { browserHistory } from 'react-router';
import PropTypes from 'prop-types';
import Select from 'react-select';
import _ from 'lodash';
import LoadingIndicatorHeader from '../loading-indicator/loading-indicator-header';
import PodFormButtons from './pod-form-buttons';
import podFormValidation from './pod-form-validation';
import ajax from '../util/util-ajax';
import util from '../util/util';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Dropzone from 'react-dropzone';
import Tooltip from "../tooltip/tooltip";
import { v4 } from 'uuid';

const jQuery = window.jQuery;

export default function PodForm (props) {
  // prop names determine which errors to show;
  const [errors, setErrors] = useState({});
  // id of the rule we are editing (0 means new rule to create);
  const [id, setId] = useState(0);
  const [name, setName] = useState(``);
  const [sourceType, setSourceType] = useState(1);
  const [sourceUrl, setSourceUrl] = useState(``);
  const [sourceFile, setSourceFile] = useState(util.podFormDefaultSourceFile);
  const [expirationDate, setExpirationDate] = useState(null);
  const [description, setDescription] = useState(``);
  const [elements, setElements] = useState([]);
  const [originalElements, setOriginalElements] = useState(JSON.stringify([]));
  const [spots, setSpots] = useState([]);
  const [originalSpots, setOriginalSpots] = useState([]);
  const [spotOptions, setSpotOptions] = useState([]);
  const [searchingForSpots, setSearchingForSpots] = useState(false);
  // tells the backend to re-process the pod
  const [spotsChanged, setSpotsChanged] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  // hide/show file upload progress bar;
  const [showProgressBar, setShowProgressBar] = useState(false);
  // if the Pod has Failed to transcode
  const [statusFailed, setStatusFailed] = useState(false);
  const [statusMessage, setStatusMessage] = useState(``);
  // have the Ajax calls resolved, and we're ready to show the form;
  const [loaded, setLoaded] = useState(false);

  useEffect( ()=>{
    const podId = props.params ? Number(props.params.id) : 0;
    setId(podId);

    // get pod data to display;
    const getPod = () => {
      if (podId === 0) {
        // if a spot was defined, then set it as the value of the SELECT
        // it should enter here when coming from a Spot page
        if (props.params && props.params.spotId) {
          util.getSpot(props.params.spotId)
            .then((data) => {
              const spot = util.returnSimpleSelectOptionsForSpots([data])[0];

              setSourceType(3);
              setElements(spotToPodElement(data[0], 1, podId));
              setSpots([spot]);
              setLoaded(true);
            });
        // otherwise just tell the component that we're done loading;
        } else {
          setElements([returnEmptyElement(0)]);
          setSpots(getDefaultSpotValue());
          setLoaded(true);
        }
      } else {
        jQuery
          .when(
            util.getPod(podId),
            util.getSpotsForPod(podId)
          )
          .then((podData, spotsData) => {
            // we dont want an empty elements array;
            // at least one value (can be empty) in the array is required;
            // this is so the form field will be rendered (one form field per item in the array);
            podData = podData[0];
            if (podData.elements.length === 0) {
              podData.elements.push(returnEmptyElement(podId));
            }
            const spotsArray = spotsData[0].results.length ?
                util.returnSimpleSelectOptionsForSpots(spotsData[0].results)
                : getDefaultSpotValue();
            const source = Number(podData.source);
            let totalSpotsDuration = spotsArray.reduce(
              (a, b) => a + Number(b.duration), 0
            );
            totalSpotsDuration = totalSpotsDuration.toFixed(2);
            setTimeout(() => {
              // default (empty) sourceFile if pod is made of spots
              const sourceFile = (podData.source_file && source !== 3) ?
                podData.source_file
                :
                util.podFormDefaultSourceFile;

              setName(podData.name);
              setSourceType(source);
              setSourceUrl(podData.source_url);
              setSourceFile(sourceFile);
              setExpirationDate(podData.expiration_date);
              setDescription(podData.description);
              setElements(podData.elements);
              setOriginalElements(JSON.stringify(
                podData.elements.filter(
                  function(element){return(element.element_id.length > 0);}
                )
              ));
              setSpots(spotsArray);
              setOriginalSpots(util.returnSimpleSelectOptions(spotsData[0].results));
              setTotalDuration(totalSpotsDuration);
              setStatusFailed(podData.aws_status === 3);
              setStatusMessage(podData.aws_status_message);
              setLoaded(true);
            }, util.delayDetail);
          });
      }
    }

    getPod();
  }, [props]);

  useEffect( ()=>{
    // greying out the empty Spots, easier with css than with react-select custom styles
    jQuery('div[class*=singleValue]:contains("Select a spot")').css(
      'color', 'hsl(0, 0%, 50%)'
    );
  }, [spots]);

  // push an empty value into the elements array;
  // this way a new INPUT will appear for the user to add the new value;
  const addContentIdNumber = () => {
    const updatedElements = Array.from(elements);

    updatedElements.push(returnEmptyElement(id));

    setElements(updatedElements);
  }

  // change a value in the elements array;
  const changeContentIdNumber = (value, index, key) => {
    const updatedElements = Array.from(elements);

    updatedElements[index][key] = value;

    setElements(updatedElements);
  }

  // remove a value from the elements array;
  const removeContentIdNumber = (index) => {
    const updatedElements = Array.from(elements);

    if (updatedElements[index].id > 0) {
      util.deleteElementFromPod(updatedElements[index].id)
        .fail(() => {
        })
        .then(() => {
          util.alertInfo('Element ' + updatedElements[index][`element_id`] + ' deleted.');
          updatedElements.splice(index, 1);

          if (updatedElements.length === 0) {
            updatedElements.push(returnEmptyElement(id));
          }

          setElements(updatedElements);
        });
    } else { // not a saved element
      updatedElements.splice(index, 1);

      setElements(updatedElements);
    }
  }

  const getSpotsInUse = () => {
    let spotIds = [];

    spots && spots.forEach((spot) => {
      // selecting only the not empty ('Select a spot') spots
      spot.value > 0 && spotIds.push(spot.value);
    });

    return spotIds;
  }

  const addSpot = () => {
    const spotsArray = Array.from(spots);
    spotsArray.push(getDefaultSpotValue()[0]);

    setSpots(spotsArray);
  }

  const returnEmptyElement = (podId) => {
    return {
      id: 0,
      pod: podId || 0,
      element_id: ``,
      protrack_id: ``,
      client_name: ``,
      spot_name: ``,
      tape: ``,
      order: ``,
      duration: ``,
      metadata: {}
    }
  }

  const spotToPodElement = (spot, order, podId) => {
    return {
      id: 0,
      pod: podId,
      element_id: spot.element_id,
      protrack_id: spot.protrack_id ? spot.protrack_id : "",
      client_name: spot.client_name ? spot.client_name : "",
      spot_name: spot.spot_name ? spot.spot_name: "",
      tape: spot.tape ? spot.tape : "",
      order: order ? order : "",
      duration: spot.duration,
      metadata: {
        // used as a way to reference spots
        spot_id: spot.id
      }
    }
  }

  // default value of the SELECT;
  const getDefaultSpotValue = () => {
    return [{
        label: `Select a spot`,
        value: 0,
        duration: 0,
        id: v4()
    }]
  }

  // updating the UI search status in the focused Select
  const getSpotSearchStatus = (searchTerm) => {
    if (searchTerm.inputValue.length === 0) {
      return `Please type a few characters to kick off remote search.`
    }
    if (searchingForSpots === true) {
      return `Searching...`
    }
    if (spotOptions.length === 0) {
      return `No results found.`
    }
    return `Something went wrong.`
  }

  // perform a backend search on the inputted character(s)
  const getSpotSearchResults = (searchTerm) => {
    // user finished typing (...more details in constructor on debounce)
    if (searchTerm.length > 0) {
      setSearchingForSpots(true);
      util
         .getSpots({
          sort: `name`,
          page: 1,
          search: searchTerm,
          aws_status: 2,
          exclude: getSpotsInUse().join(","),
        })
        .then((data) => {
          setSearchingForSpots(false);
          setSpotOptions(util.returnSimpleSelectOptionsForSpots(data.results));
        })
    } else {
      // user deletes the previously inputted search keywords
      setSearchingForSpots(false);
      setSpotOptions([]);
    }
  }

  // delaying the spot search call until the user has stopped typing (after 250 msec)
  const getSpotSearchResultsDelayed = useCallback(
    _.debounce(getSpotSearchResults, 250),
    [spots]
  )

  // updating the State when the user has selected a Spot out of the search results
  const handleSpotsOnValueChange = (valueChanged, currentSpotId) => {
    let spotsArray;
    let spotsElement = [];
    let elementsOrder = 1;
    let spotsTotalDuration = 0;

    // determine which Spot value was changed, and only update that select
    spotsArray = spots.map( (spot) => {
      // the current spot can have its value changed, or deleted
      if (spot.id === currentSpotId) {
        // value changed - updating that spot with the new value
        if (valueChanged) {
          return valueChanged;
        } else {
          // value deleted - setting it back to default
          return getDefaultSpotValue()[0];
        }
      } else {
        // do nothing for the non-current spots
        return spot;
      }
    });

    // updating the Elements (Spots Metadata), for all the valid (non-empty) Spots
    spotsArray.forEach((spot) => {
      let spotElement;

      if (spot.value > 0) {
        // checking if that Element already exists (for Edit Pod case)
        for (let stateElement of elements) {
          if (stateElement.metadata.spot_id === spot.id) {
            spotElement = stateElement;
            spotElement[`order`] = elementsOrder;
            elementsOrder++;
            break;
          }
        }
        // if it doesn't exist, creating a new one
        if (!spotElement) {
          spotElement = spotToPodElement(spot, elementsOrder, id);
          elementsOrder++;
        }

        spotsElement.push(spotElement);

        spotsTotalDuration += Number(spot.duration);
      }
    });

    // updating the State in order to re-render the Spots and the Elements
    setSpotsChanged(true);
    setSpots(spotsArray);
    setElements(spotsElement);
    setSpotOptions([]);
    setTotalDuration(spotsTotalDuration.toFixed(2));
  }

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      const filesize = Math.ceil(file.size / (1024 * 1024)); // convert bytes into megabytes;

      // see if there are any errors before proceeding;
      const validationResult = podFormValidation({ filesize: filesize });

      // danger will robinson, we have a file size error;
      if (validationResult.hasErrors) {
        // display the error(s) - existing and new;
        // and remove any previous value for the file upload;
        setErrors(_.merge({}, errors, validationResult.errors));
        setSourceFile(util.podFormDefaultSourceFile);

        // stop any further processing;
        return false;
      }

      const report = {
        filename: file.name,
        filetype: file.type
      };

      // sourceFile isn't known yet, it will be determined in the response from the Django server;
      let sourceFileBackend = ``;

      // get the data needed to post to S3;
      util.getS3Url(report)
        .then((data) => {
          const s3Report = new FormData();
          const s3Url = data.data.url;

          // take whatever was returned from Django;
          // and include it in the post to S3;
          for (const key in data.data.fields) {
            s3Report.append(key, data.data.fields[key]);
          }

          sourceFileBackend = data.url;

          // also include the file;
          s3Report.append(`file`, file);

          // add a progress callback;
          const progressCallback = () => {
            const xhr = new window.XMLHttpRequest();

            xhr.upload.addEventListener(`progress`, (progressEvent) => {
              if (progressEvent.lengthComputable) {
                const percentComplete = Math.ceil(progressEvent.loaded / progressEvent.total * 100);

                // update the progress indicator;
                setProgress(percentComplete);
              }
            }, false);

            return xhr;
          };

          // turn on the progress indicator;
          setProgress(0);
          setShowProgressBar(true);

          // start the file upload;
          return util
            .saveFileToS3(s3Report, s3Url, progressCallback)
            .fail(() => {
              setShowProgressBar(false);
              setSourceFile(util.podFormDefaultSourceFile);
            });
        })
        .then(() => {
          // let the user read the completed message for this long;
          const timeToShowCompletedUpload = 1500;

          setTimeout(() => {
            // take the existing errors and remove the file error;
            // the file just finished uploading, there aren't anymore file errors;
            const errorsArray = _.merge({}, errors, validationResult.errors);

            delete errorsArray.sourceFile;

            // then turn off the progress indicator and set the filename to display;
            setErrors(errorsArray);
            setShowProgressBar(false);
            setSourceFile(sourceFileBackend);
          }, timeToShowCompletedUpload);
        });
    }
  }

  // submit the form;
  const onSubmit = () => {
    // we'll need the Pod Id later, after it's created on the backend
    let podId = null;

    // build the object to send to the server;
    const report = {
      "description": description.trim(),
      "elements": [],
      "id": id,
      "name": name.trim(),
      "showProgressBar": showProgressBar, // used by validation only;
      "source": sourceType,
      "source_file": sourceType === 2 ? sourceFile : null,
      "source_url": sourceType === 1 ? sourceUrl : null,
      "sourceFile": sourceType === 2 ? sourceFile : null, // used by validation only;
      "sourceUrl": sourceType === 1 ? sourceUrl : null, // used by validation only;
      "expiration_date": expirationDate === null ? null : moment(expirationDate).format(util.dateFormatComputer),
      "spots": [],
      "totalDuration": totalDuration,
      "spots_changed": sourceType === 3 && spotsChanged
    };

    // clean up data before passing for validation
    elements.forEach((element) => {
      const reportElements = report[`elements`];

      // clean data before saving
      element.pod = report.id;
      element.element_id = element.element_id.toString().trim();
      element.protrack_id = element.protrack_id.trim();
      element.client_name = element.client_name.trim();
      element.spot_name = element.spot_name.trim();
      element.tape = element.tape.toString().trim();
      element.order = element.order.toString().trim();
      element.duration = element.duration.toString().trim();
      reportElements.push(element);
    });

    spots && spots.forEach((spot) => {
      // selecting only the not empty ('Select a spot') spots
      spot.value > 0 && report.spots.push(spot.value);
    });

    // see if there are any errors before proceeding;
    const validationResult = podFormValidation(report);

    // danger will robinson, we have errors;
    if (validationResult.hasErrors) {
      setErrors(validationResult.errors);

      // stop any further processing;
      return false;

    // good to go, clear any existing errors;
    } else {
      setErrors({});
    }

    // remove empty values for the elements before passing to Django;
    // Django can't deal with an empty value in the array;
    report[`elements`] = report[`elements`].filter( function(element) {
      return (element.element_id.length > 0);
    });

    // put the button into a processing state;
    const $button = jQuery(`.btn-primary`).button(`loading`);

    // save the form data;
    util.savePod(report.id, report)
      // if there is a backend error (validation,...) on Pod save
      .fail((error) => {
        const serializerNameExistsError = "A Pod with this name already exists.";
        const api_error = _.get(error, "responseJSON.Validation_Error[0]")

        // if the name already exists
        if (serializerNameExistsError === api_error) {
          setErrors(_.merge({}, errors, { name: api_error}));
        // if there is a different backend (4xx, 5xx) error
        } else {
          ajax.onFail(error);
        }

        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);

        // stop any further processing, as the Pod was not saved in the DB
        return false;
      })
      .then((data) => {
        // needed for subsequent ajax calls;
        podId = data.id;

        // toggle the pod id notification, so that we can track its status
        // and send a notification once it's ready / failed
        util.savePodNotification(podId, false);

        // only need to update the Elements if they were changed
        if (originalElements !== JSON.stringify(report[`elements`])) {
          // make sure we actually have something meaningful to save
          if (report[`elements`].length > 0) {
            // if this is a new pod, assign the proper podId
            report[`elements`].forEach((element, index) => {
              if (element.pod === 0) {
                report[`elements`][index].pod = podId;
              }
            });

            // updating the Elements (do a clear first, then create the new ones)
            util.updateElementsForPod(podId, report[`elements`])
          }
        }
      })
      .fail(() => {
        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);
      })
      .then(() => {
        const removeRequests = originalSpots.map((spot) => {
          return util.deleteSpotFromPod(podId, spot.value);
        });
        // start the removals;
        return jQuery.when.apply(jQuery, removeRequests);
      })
      .fail(() => {
        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);
      })
      .then(() => {
        if (report.spots.length === 0 && report.source !== 3) {
          return Promise.resolve()
        }
        // save spots to Pod. order must be kept!
        return util.saveSpotsToPod(podId, report.spots);
      })
      .fail(() => {
        setTimeout(() => {
          // reset the button on fail so it can be clicked again;
          $button.button(`reset`);
        }, util.delayForm);
      })
      .then(() => {
        setTimeout(() => {
          const newUrl = util.returnPodDetailUrl(podId);

          // load the detail page;
          browserHistory.push(newUrl);
        }, util.delayForm);
      })
  }

  // cancel this action (do nothing and go somewhere else);
  const onCancel = () => {
    const newUrl = id === 0 ? util.returnPodListUrl() : util.returnPodDetailUrl(id);

    browserHistory.push(newUrl);
  }

  if (loaded === false) {
    return (
      <LoadingIndicatorHeader />
    );
  } else {
    const displayErrors = util.formatVideoError(statusMessage);
    return (
      <form className="pod-form" encType="multipart/form-data" action="#">
        <div className="row">
          <div className="col-sm-6">
            <h4 className="page__title">{ id === 0 ? `New Pod` : `Edit Pod` }</h4>
          </div>
          <div className="col-sm-6 hidden-xs">
            <PodFormButtons
              onCancel={ () => onCancel() }
              onSubmit={ () => onSubmit() } />
          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-sm-8">
            <div className={ errors.name ? `form-group has-error` : `form-group` }>
              <label className="control-label" htmlFor="name">Pod Name</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={ name }
                autoFocus
                onChange={ (e) => setName(e.target.value)}
                maxLength="100"
                readOnly={ statusFailed } />
              <p className={ errors.name ? `help-block` : `hidden` }>
                { errors.name }
              </p>
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label className="control-label" htmlFor="expirationDate">Expiration Date</label>
              <div className="input-group">
                <div
                  className="input-group-addon"
                  onClick={ () => document.querySelector(`#expirationDate`).focus() }>
                  <i className="fa fa-calendar" aria-hidden="true"></i>
                </div>
                <div className="datepicker__container">
                  {<DatePicker
                    id="expirationDate"
                    className="form-control"
                    popperClassName="pod-form__datepicker"
                    fixedHeight
                    selected={ expirationDate === null ? null : moment(expirationDate) }
                    onChange={ (value) => setExpirationDate(value) } /> }
                </div>
              </div>
              <p className={ errors.startDate ? `help-block` : `hidden` }>
                Start Date must be a valid date, and less than End Date.
              </p>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label className="control-label">Source</label>
              <div className="radio">
                <label className="radio-inline">
                  <input
                    type="radio"
                    name="source"
                    value="1"
                    checked={ sourceType === 1 }
                    onChange={ (e) => {
                      const comingFromSourceType = sourceType;
                      let elementsArray = [];
                      if (comingFromSourceType === 2) {
                        elementsArray = elements;
                      } else if (comingFromSourceType === 3) {
                        elementsArray = [ returnEmptyElement(0) ];
                      }
                      setSourceType(Number(e.target.value));
                      setElements(elementsArray);
                      setSpots([]);
                    } }
                  /> URL
                </label>
                <label className="radio-inline">
                  <input
                    type="radio"
                    name="source"
                    value="2"
                    checked={ sourceType === 2 }
                    onChange={ (e) => {
                      const comingFromSourceType = sourceType;
                      let elementsArray = [];
                      if (comingFromSourceType === 1) {
                        elementsArray = elements;
                      } else if (comingFromSourceType === 3) {
                        elementsArray = [ returnEmptyElement(0) ];
                      }
                      setSourceType(Number(e.target.value));
                      setElements(elementsArray);
                      setSpots([]);
                    } }
                  /> File
                </label>
                {util.spotsAvailable() &&
                <label className="radio-inline">
                  <input
                    type="radio"
                    name="source"
                    value="3"
                    checked={ sourceType === 3 }
                    onChange={ (e) => {
                      setSourceType(Number(e.target.value));
                      setElements([]);
                      setSpots(getDefaultSpotValue());
                      setSpotOptions([]);
                      setTotalDuration(0);
                    } }
                  /> Spots
                </label>}
              </div>
            </div>
          </div>
          <div className="col-sm-8">
            <div id="pod-form__url" className={ sourceType === 1 ? `` : `hidden` }>
              <div className={ errors.sourceUrl ? `form-group has-error` : `form-group` }>
                <label className="control-label" htmlFor="url">URL</label>
                <input
                  type="text"
                  id="url"
                  className="form-control"
                  value={ sourceUrl ? sourceUrl : ``}
                  maxLength="200"
                  onChange={ (e) => setSourceUrl(e.target.value) }
                />
                <p
                  className={errors.sourceUrl ? `help-block` : `hidden`}
                >
                  A valid URL is required. Maximum of 200 characters.
                </p>
              </div>
            </div>

            <div id="pod-form__file" className={ sourceType === 2 ? `` : `hidden` }>
              <div className={ errors.sourceFile ? `form-group has-error` : `form-group` }>
                <label className="control-label">File</label>
                {
                  sourceFile !== util.podFormDefaultSourceFile &&
                  <span>&nbsp;(
                    <button
                      type="button"
                      className="btn btn-link button_as_link"
                      onClick={ () => setSourceFile(util.podFormDefaultSourceFile)}
                    >
                      Replace file
                    </button>
                  )</span>
                }
                <div className="pod-form__file">
                  {
                    sourceType === 2 && sourceFile === util.podFormDefaultSourceFile ? (
                      <Dropzone
                        className="form-control pod-form__file-dropzone"
                        activeClassName="pod-form__file-dropzone--active"
                        onDrop={ (acceptedFiles, rejectedFiles) => onDrop(acceptedFiles, rejectedFiles) }
                        multiple={ false }>
                        <div dangerouslySetInnerHTML={ { __html: sourceFile } }></div>
                      </Dropzone>
                    ) : (
                      <div
                        className="form-control pod-form__file-dropzone"
                        dangerouslySetInnerHTML={ { __html: sourceFile } }
                      >
                      </div>
                    )
                  }

                  {
                    showProgressBar ? (
                      <div className="progress">
                        <div
                          className="progress-bar progress-bar-striped active"
                          role="progressbar"
                          aria-valuenow={ progress }
                          aria-valuemin="0"
                          aria-valuemax="100"
                          style={ { width: `${ progress }%` } }
                        >
                          { progress }% Complete
                        </div>
                      </div>
                    ) :
                      null
                  }
                </div>
                <p className={ errors.sourceFile ? `help-block` : `hidden` }>
                  {
                    errors.uploading ? (
                      <span>File is still uploading.</span>
                    ) : (
                      <span>
                        File is required, and must be less than
                        { util.podFormMaxFilesize / 1024 }GB.
                      </span>
                    )
                  }
                </p>
              </div>
            </div>
            <div id="pod-form__pods" className={ sourceType === 3 ? `` : `hidden` }>
              <div className={ errors.spots ? `form-group has-error` : `form-group` }>
                <label className="control-label">
                Add Spots&nbsp;
                <i
                  data-tip="Only spots with status ready are available."
                  data-for="addSpotToNewPod"
                  className="glyphicon glyphicon-question-sign"
                  aria-hidden="true"
                ></i>
              </label>
              <Tooltip offset_top={"-6"} id="addSpotToNewPod"/>
              <div className={
                errors.spots ? `pod-spots-duration text-danger` : `pod-spots-duration`
              }>
                Total Pod duration: {totalDuration} seconds.
              </div>
              <div className="well">
                {
                  spots.map((spot) => (
                    <div className="form-group" key={ spot.id }>
                      <Select
                        name={ spot.id }
                        value={ spot }
                        options={ spotOptions }
                        theme="bootstrap3"
                        isClearable={ true }
                        blurInputOnSelect
                        filterOption={
                          (dbOption, userInput) => {return dbOption;}
                        }
                        noOptionsMessage={
                          (searchTerm) => {
                            return (
                              <div className="no-results-found">
                                { getSpotSearchStatus(searchTerm) }
                              </div>
                            );
                          }
                        }
                        onInputChange={
                          (searchTerm) => {
                            // updating the search status on the UI
                            setSearchingForSpots(true);
                            // making the backend call, with delay
                            getSpotSearchResultsDelayed(searchTerm);
                          }
                        }
                        onChange={
                          (valueChanged, currentSpot) => {
                            handleSpotsOnValueChange(valueChanged, currentSpot.name);
                          }
                        }
                      />
                    </div>
                  ))
                }
              </div>
              <p className={ errors.spots ? `help-block` : `hidden` }>
                Total Pod duration must be between {util.podFormMinDuration} and {util.podFormMaxDuration} seconds.
              </p>
              <button
                type="button"
                id="campaign-form__add--pod"
                className="btn btn-default"
                onClick={ () => addSpot() }>
                <i className="fa fa-plus" aria-hidden="true"></i>
                Add Another Spot
              </button>
              </div>
            </div>
          </div>
          {displayErrors.length ? (
            <div className="col-xs-12">
              <div className="page__status-message">
                <ul>
                  {displayErrors.map((errorMessage, i) => (
                    <li
                      key={errorMessage}
                    >
                      { errorMessage }
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </div>
        {errors["elements"] ? (
            <div className="col-xs-12 m-b-10">
                <div className="page__status-message">
                  Element IDs must be unique within a Pod.
                </div>
            </div>
        ) : null}
        <div className={ elements.length > 0 ? `row` : `row hidden`} >
          <div className="col-sm-12">
            <div className="form-group">
              <div className="well">
                {
                  elements.map((element, index) => (
                    <div className="form-group" key={ index }>
                      {
                        <div>
                          {
                            index > 0 ? (
                              <hr className="visible-xs-block" />
                            ) : (``)
                          }
                          <div className="row">
                            <div className="col-md-3 col-xs-12">
                              <div className={
                                errors['element' + index] ? `form-group has-error` : `form-group`
                              }>
                                <label
                                  className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                  htmlFor={ `element_id${ index }` }>
                                  Element ID*
                                </label>
                                <div className={
                                  `${sourceType !== 3 && (index > 0 || element.id > 0) ?
                                    `input-group` 
                                    :
                                    ``}`
                                }>
                                  <input
                                    type="text"
                                    id={ `element_id${ index }` }
                                    className="form-control"
                                    value={ element.element_id }
                                    maxLength="100"
                                    onChange={
                                      (e) => changeContentIdNumber(
                                        e.target.value, index, 'element_id'
                                      )
                                  }
                                    readOnly={ statusFailed } />
                                  {
                                    sourceType !== 3 && (index > 0 || element.id > 0) ? (
                                    <span
                                      id={ `element_id${ index }--remove` }
                                      className="input-group-addon"
                                      onClick={() => removeContentIdNumber(index) }>&times;</span>
                                    ) : ( `` )
                                  }
                                </div>
                              </div>
                            </div>

                            <div className="col-md-2 col-xs-4">
                              <label
                                className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                htmlFor={ `protrack_id${ index }` }>
                                Protrack ID
                              </label>
                              <input
                                type="text"
                                id={`protrack_id${ index }"`}
                                className="form-control"
                                value={ element.protrack_id }
                                maxLength="100"
                                onChange={
                                  (e) => changeContentIdNumber(
                                    e.target.value, index, 'protrack_id'
                                  )
                                }
                                readOnly={ statusFailed } />
                            </div>

                            <div className="col-md-2 col-xs-4">
                              <label
                                className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                htmlFor={ `client_name${ index }` }>
                                Client Name
                              </label>
                              <input
                                type="text"
                                id={`client_name${ index }`}
                                className="form-control"
                                value={ element.client_name }
                                maxLength="255"
                                onChange={
                                  (e) => changeContentIdNumber(
                                    e.target.value, index, 'client_name'
                                  )
                                }
                                readOnly={ statusFailed } />
                            </div>

                            <div className="col-md-2 col-xs-4">
                              <label
                                className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                htmlFor={ `spot_name${ index }` }>
                                Spot Name
                              </label>
                              <input
                                type="text"
                                id={`spot_name${ index }`}
                                className="form-control"
                                value={ element.spot_name }
                                maxLength="255"
                                onChange={
                                  (e) => changeContentIdNumber(
                                    e.target.value, index, 'spot_name'
                                  )
                                }
                                readOnly={ statusFailed || sourceType === 3 } />
                            </div>

                            <div className="col-md-1 col-xs-4">
                              <label
                                className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                htmlFor={ `tape${ index }` }>
                                Tape
                              </label>
                              <input
                                type="text"
                                id={`tape${ index }`}
                                className="form-control"
                                value={ element.tape }
                                maxLength="255"
                                onChange={
                                  (e) => changeContentIdNumber(
                                    e.target.value, index, 'tape'
                                  )
                                }
                                readOnly={ statusFailed } />
                            </div>

                            <div className="col-md-1 col-xs-4">
                              <label
                                className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                htmlFor={ `order${ index }` }>
                                Order
                              </label>
                              <input
                                type="text"
                                id={`order${ index }`}
                                className="form-control"
                                value={ element.order }
                                maxLength="255"
                                onChange={
                                  (e) => changeContentIdNumber(
                                    e.target.value, index, 'order'
                                  )
                                }
                                readOnly={ statusFailed || sourceType === 3} />
                            </div>

                            <div className="col-md-1 col-xs-4">
                              <label
                                className={`control-label ${index > 0 ? `visible-xs` : ``}`}
                                htmlFor={ `duration${ index }` }>
                                Duration
                              </label>
                              <input
                                type="text"
                                id={`duration${ index }`}
                                className="form-control"
                                value={ element.duration }
                                maxLength="255"
                                onChange={
                                  (e) => changeContentIdNumber(
                                    e.target.value, index, 'duration'
                                  )
                                }
                                readOnly={ statusFailed || sourceType === 3 } />
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  ))
                }
              </div>
              {
                // disable button if Pod is failed or source type is "spots"
                statusFailed === false && sourceType !== 3 ? (
                  <button
                    type="button"
                    id="id--add"
                    className="btn btn-default"
                    onClick={ () => addContentIdNumber() }>
                    <i className="fa fa-plus" aria-hidden="true"></i>
                    Add Another Element
                  </button>
                ) : null
              }
            </div>
            <hr className="visible-xs-block" />
          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-sm-12">
            <div className="form-group">
              <label
                className="control-label control-label--optional"
                htmlFor="description"
              >
                Description
              </label>
              <textarea
                id="description"
                className="form-control"
                value={ description }
                rows="3"
                onChange={ (e) => setDescription(e.target.value) }
                readOnly={ statusFailed }>{ description }</textarea>
            </div>
          </div>
        </div>

        <hr />

        <PodFormButtons
          onCancel={ () => onCancel() }
          onSubmit={ () => onSubmit() } />
      </form>
    );
  }
}

PodForm.propTypes = {
  params: PropTypes.object
};
